// Module 4.1a — Kill Sheet Editor (Operational Engineering layer).
//
// Sections:
//   1. Identity (name, sheet #, revision, prepared-by, date)
//   2. Engineering Basis (Kill Method, Fluid Basis, Influx, Well-Control Std)
//   3. Source Snapshot — frozen geometry pulled from Well Profile + BHA.
//      Re-pull is explicit (user clicks "↻ Re-pull from sources"). The
//      sheet's geometry never auto-mutates from upstream edits.
//   4. Frozen Inputs — pressures, pump, volumes, margin.
//   5. Outputs — live KMW / ICP / FCP / strokes / times.
//   6. Engineering Details / Calculation Trace — collapsible panel showing
//      formula + exact inputs + provenance versions per output.
//   7. Status + Lock toggle.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, AlertTriangle, Trash, RefreshCcw, ChevronDown, ChevronRight, Printer, GitBranch,
} from "lucide-react";
import { Field, TextInput, TextArea, DateTimeSplitInput } from "@/components/ReportPrimitives";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { useStackMode } from "@/lib/stackModeContext";
import {
  emptyKillSheet,
  KILL_METHODS, FLUID_BASES, INFLUX_TYPES, KILLSHEET_STATUSES, WELL_CONTROL_STANDARDS,
  REVISION_REASONS, REVISION_REASON_LABELS,
} from "@/lib/killsheet/schema";
import {
  createKillSheet, getKillSheet, updateKillSheet,
  deleteKillSheet,
  createKillSheetRevision, diffKillSheetFromParent,
  synchronizeKillSheet, applyEngineeringContextPreview,
} from "@/lib/killsheet/storage";
import FloatingActionToolbar from "@/components/FloatingActionToolbar";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { buildKickState } from "@/lib/rce/buildKickState";
import { computeKillSheetOutputs } from "@/lib/killsheet/compute";
import { isProActive } from "@/lib/featureAccess";
import { useEntitlement } from "@/lib/entitlement";
import SubscribeModal from "@/components/SubscribeModal";
import PremiumResultsLock from "@/components/PremiumResultsLock";
import { ScheduleChart } from "@/components/killsheet/ScheduleChart";
import {
  buildSurfaceLineRow, firstKillRowIndex, SURFACE_LINE_ROW_LABEL,
  SURFACE_LINE_RESET_REMINDER, DRILL_STRING_ZERO_LABEL,
} from "@/lib/killsheet/surfaceLineRow";
import { nextWellSectionHash } from "@/lib/well/completion";

function NativeSelect({ value, onChange, options, testId, disabled }) {
  return (
    <select value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            data-testid={testId}
            className="input-rs">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
function NumIn(props) { return <TextInput type="number" {...props} />; }

const _fmt = (v, digits = 2) => {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
};
const _fmtInt = (v) => {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString();
};

export default function KillSheetOpEditor() {
  const navigate = useNavigate();
  const { stackMode } = useStackMode();
  const { wellId, ksId } = useParams();
  const isNew = !ksId;

  const [well, setWell] = useState(() => getWellProfile(wellId));
  const [rec,  setRec]  = useState(() => {
    if (isNew) {
      // Phase 1 — Single canonical pipeline. The draft opens with empty
      // engineering inputs; Synchronize Engineering Data populates them
      // through the SAME helper that createKillSheet / synchronizeKillSheet
      // use. No separate buildWellState pre-fill, no alternate path.
      const seed = emptyKillSheet();
      return { ...seed, wellId };
    }
    const row = getKillSheet(ksId);
    return row || emptyKillSheet();
  });
  const [errors, setErrors] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [traceOpen, setTraceOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(true);
  // Subscribe to the entitlement instead of reading the module-level cache:
  // on a cold deep-link (opening a saved kill sheet URL directly) the cache
  // is still `loading` when this editor first renders, and nothing in this
  // component re-rendered afterwards — so every Pro section (Live Outputs,
  // MAASP, Driller's Schedule, Surface Line Displacement) stayed locked for
  // an entitled user until an unrelated interaction happened to re-render.
  const pro = isProActive(useEntitlement());
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    const row = getKillSheet(ksId);
    if (row) setRec(row); else setNotFound(true);
  }, [ksId, isNew]);

  // Live recompute on every render — computeKillSheetOutputs is pure.
  const liveOutputs = useMemo(() => computeKillSheetOutputs(rec), [rec]);

  // Pass E — "What Changed" diff against parent revision (only when saved).
  const diff = useMemo(() => {
    if (isNew || !rec.parentId) return { hasParent: false, changes: [] };
    try { return diffKillSheetFromParent(rec.id); }
    catch { return { hasParent: false, changes: [] }; }
  }, [rec.id, rec.parentId, rec.updatedAt, isNew]);

  const locked = rec.status === "locked";

  // -- mutation helpers -----------------------------------------------------
  const set       = (patch) => setRec((r) => ({ ...r, ...patch }));

  // RC-1 · Unsaved Changes Protection (presentation only).
  const unsaved = useUnsavedChanges({ data: rec, enabled: !notFound, onSave: () => handleSave() });
  const setBasis  = (patch) => setRec((r) => ({ ...r, basis:  { ...r.basis,  ...patch } }));
  const setInputs = (patch) => setRec((r) => ({ ...r, inputs: { ...r.inputs, ...patch } }));

  // RCE-4 — Engineering session context. Passed into createKillSheet so
  // the kill sheet freezes a snapshotId + sourceModules on first save.
  const { session } = useEngineeringSession();

  // Phase 1 — Single canonical Engineering Context builder. EVERY
  // synchronization path (create, save, sync, reopen) reads from this
  // exact memo. There is no other rceCtx construction in the editor.
  const kickState = useMemo(
    () => buildKickState(session, wellId),
    [session, wellId]
  );
  const rceCtx = kickState.ids;

  // Auto-apply Engineering Context on open for a brand-new (unsaved) Kill
  // Sheet, so operators get inheritable fields populated without having to
  // click "Synchronize from session" first. Uses the exact same pure,
  // non-persisting helper as handleSynchronize's isNew branch — only
  // inheritable engineering fields are filled, event-specific inputs
  // (SIDPP, SICP, influx/pit gain, etc.) stay blank, and nothing is written
  // to storage merely by opening this screen. Fires once on mount only, so
  // it never re-clobbers fields the operator has since edited if rceCtx
  // changes later — that's still what the manual Synchronize button is for.
  const autoContextAppliedRef = useRef(false);
  useEffect(() => {
    if (!isNew || autoContextAppliedRef.current) return;
    autoContextAppliedRef.current = true;
    setRec((r) => applyEngineeringContextPreview(r, rceCtx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  // -- actions --------------------------------------------------------------
  const handleSave = () => {
    // Gate the Create/Save action itself — Free users can view + fill in
    // every input above (SIDPP, SICP, influx/pit gain, etc.); persisting a
    // kill sheet record (and the live outputs derived from it) is Pro-only.
    if (!pro) { setUpgradeOpen(true); return false; }
    try {
      const saved = isNew
        ? createKillSheet({ ...rec, rceContext: rceCtx })
        : updateKillSheet(ksId, rec);
      setErrors([]);
      unsaved.markSaved(saved || rec);
      // RC-1 Commercial Workflow QA — return to Well Profile workbook if the
      // operator arrived from there.
      const returnToWell = new URLSearchParams(window.location.search).get("return") === "well";
      if (returnToWell) {
        navigate(nextWellSectionHash(wellId, "killsheet"));
      } else {
        navigate(`/wells/${wellId}/kill-sheets/${saved.id}`);
      }
      return true;
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed."]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
  };

  // Surface-line displacement row for the Driller's Schedule — the Card 4
  // volume and its stroke/time figures, recomputed from the live record.
  // Display-only: no engine output or pressure value is affected.
  const surfaceLineRow = useMemo(
    () => buildSurfaceLineRow({
      rigId: rec?.contextSnapshot?.sessionContextIds?.rigId || "",
      pumpOutputBblStk: rec?.inputs?.pumpOutputBblStk,
      pumpRateSpm: rec?.inputs?.pumpRateSpm,
    }),
    [rec],
  );

  const handleDelete = () => {
    if (!ksId) return;
    if (!window.confirm(`Delete kill sheet "${rec.name || "(unnamed)"}"?`)) return;
    deleteKillSheet(ksId);
    navigate(`/wells/${wellId}/kill-sheets`);
  };

  const handleSynchronize = () => {
    // Phase 1 — Single canonical Engineering Context pipeline.
    //
    //   isNew  → applyEngineeringContextPreview (no persistence, pure)
    //   saved  → synchronizeKillSheet (persists)
    //
    // BOTH branches feed the SAME `_applyEngineeringContext` helper inside
    // storage.js so the resulting `inputs` / `basis` / `contextSnapshot` is
    // byte-identical between paths. There is no alternate code in this
    // function — the two branches differ only in whether they write to
    // localStorage.
    if (isNew) {
      setRec((r) => applyEngineeringContextPreview(r, rceCtx));
      return;
    }
    if (!window.confirm("Synchronize Engineering Data?\n\nThis refreshes every inheritable field from the active Engineering Session — Project, Rig, Well, BHA, Drill String, Mud Program and Settings. A new Engineering Snapshot ID will be issued for the audit trail.")) return;
    try {
      const fresh = synchronizeKillSheet(ksId, rceCtx);
      setRec(fresh);
      setErrors([]);
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Synchronize failed."]);
    }
  };

  const handleCreateRevision = () => {
    if (isNew) return;
    const reason = window.prompt(
      "Reason for revision (paste one of):\n" +
      REVISION_REASONS.filter(Boolean).map((r) => "  " + r).join("\n") +
      "\n\n(Leave blank to skip)",
      "supervisor-review");
    if (reason === null) return;        // user cancelled
    if (reason && !REVISION_REASONS.includes(reason)) {
      setErrors([`Unknown revision reason "${reason}". Pick from the listed values or leave blank.`]);
      return;
    }
    const notes = window.prompt("Optional revision notes (free text):", "") || "";
    try {
      const child = createKillSheetRevision(ksId, { reason, notes });
      navigate(`/wells/${wellId}/kill-sheets/${child.id}`);
    } catch (e) { setErrors(e.validationErrors || [e.message]); }
  };

  if (notFound) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Kill sheet not found.{" "}
        <Link to={`/wells/${wellId}/kill-sheets`} className="text-amber-400">Back to Kill Sheets</Link>.
      </div>
    );
  }

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view this kill sheet, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }

  return (
    <div data-testid="ks-editor" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/kill-sheets`)}
                data-testid="ks-editor-back"
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Kill Sheets
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button type="button"
                    onClick={handleDelete}
                    data-testid="ks-editor-delete"
                    disabled={locked}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] text-red-300 border border-red-500/30 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed">
              <Trash className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 4.1a · {well.wellName || "Well"}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)]">
          {isNew ? "New Kill Sheet" : (rec.name || "(unnamed kill sheet)")}
        </h1>
      </div>

      {errors.length > 0 && (
        <div data-testid="ks-editor-errors"
             className="card-rs p-3 border-red-500/30 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <ul className="text-xs text-red-300 list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* RCE-4 · Engineering Snapshot strip */}
      {(rec.contextSnapshot?.snapshotId || session) && (
        <div
          data-testid="ks-rce-snapshot-strip"
          className="card-rs p-3"
          style={{
            background: "rgba(255,179,0,0.06)",
            borderColor: "var(--rs-border)",
            borderLeft: "2px solid var(--rs-amber)",
          }}
        >
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--rs-amber)" }}>
              Engineering Snapshot · {rec.contextSnapshot?.contextVersion || "RCE-1.0"}
              {rec.contextSnapshot?.frozen ? " · Frozen" : " · Pending freeze"}
            </span>
            {rec.contextSnapshot?.snapshotId && (
              <span data-testid="ks-rce-snapshot-id" className="font-mono text-[10.5px] text-[var(--rs-text-1)]">
                {rec.contextSnapshot.snapshotId}
              </span>
            )}
          </div>
          <div className="text-[11px] text-[var(--rs-text-2)] grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
            {rec.contextSnapshot?.snapshotId ? (
              <>
                <span><b>Operator:</b> {rec.contextSnapshot.project.operator || "—"}</span>
                <span><b>Well:</b> {rec.contextSnapshot.well.name || "—"}</span>
                <span><b>Rig:</b> {rec.contextSnapshot.rig.name || "—"}</span>
                <span><b>Section:</b> {rec.contextSnapshot.well.holeSection || "—"}</span>
                <span><b>Depth MD:</b> {rec.contextSnapshot.well.currentMdFt || "—"}</span>
                <span><b>MW:</b> {rec.contextSnapshot.mud.weightInPpg || "—"} ppg</span>
                <span className="col-span-2 sm:col-span-3">
                  <b>Sources:</b> {(rec.contextSnapshot.sourceModules || []).join(" · ") || "—"}
                </span>
                <span className="col-span-2 sm:col-span-3 text-[var(--rs-text-3)]">
                  Captured: {rec.contextSnapshot.capturedAt ? new Date(rec.contextSnapshot.capturedAt).toLocaleString() : "—"}
                </span>
              </>
            ) : (
              <span className="col-span-3 text-[var(--rs-text-3)]">
                Live preview — engineering context will be frozen on first save (RCE-1.0).
              </span>
            )}
          </div>
          {!isNew && !locked && (
            <button
              type="button"
              data-testid="ks-synchronize"
              onClick={handleSynchronize}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-semibold border border-amber-400/60 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Synchronize Engineering Data
            </button>
          )}
        </div>
      )}

      {/* Pass E — "What Changed" diff strip vs parent revision */}
      {diff.hasParent && (
        <WhatChangedStrip diff={diff} rec={rec} />
      )}

      {/* 1. Identity ------------------------------------------------------ */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Sheet name (required)">
            <TextInput value={rec.name} onChange={(v) => set({ name: v })} testId="ks-name" />
          </Field>
          <Field label="Sheet number">
            <TextInput value={rec.sheetNumber} onChange={(v) => set({ sheetNumber: v })} testId="ks-sheetNumber" />
          </Field>
          <Field label="Revision">
            <TextInput value={rec.revision} onChange={(v) => set({ revision: v })} testId="ks-revision" />
          </Field>
          <Field label="Prepared by">
            <TextInput value={rec.preparedBy} onChange={(v) => set({ preparedBy: v })} testId="ks-preparedBy" />
          </Field>
          <Field label="Date / time">
            <DateTimeSplitInput value={rec.dateTime} onChange={(v) => set({ dateTime: v })} testId="ks-dateTime" />
          </Field>
          <Field label="Status">
            <NativeSelect value={rec.status} onChange={(v) => set({ status: v })}
                          options={KILLSHEET_STATUSES.map((s) => ({ value: s, label: s }))}
                          testId="ks-status" disabled={locked} />
          </Field>
          {!isNew && rec.parentId && (
            <Field label="Reason for revision (optional)"
                   helper="Picked when creating the revision. Editable until the sheet is approved or locked.">
              <NativeSelect value={rec.revisionReason || ""}
                            onChange={(v) => set({ revisionReason: v })}
                            options={REVISION_REASONS.map((r) => ({
                              value: r, label: REVISION_REASON_LABELS[r] || r,
                            }))}
                            testId="ks-revisionReason" disabled={locked} />
            </Field>
          )}
        </div>
      </section>

      {/* 2. Engineering Basis -------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Engineering Basis</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Kill method (required)">
            <NativeSelect value={rec.basis?.killMethod}
                          onChange={(v) => setBasis({ killMethod: v })}
                          options={KILL_METHODS.map((m) => ({ value: m, label: m }))}
                          testId="ks-basis-killMethod" disabled={locked} />
          </Field>
          <Field label="Fluid basis (required)">
            <NativeSelect value={rec.basis?.fluidBasis}
                          onChange={(v) => setBasis({ fluidBasis: v })}
                          options={FLUID_BASES.map((m) => ({ value: m, label: m }))}
                          testId="ks-basis-fluidBasis" disabled={locked} />
          </Field>
          <Field label="Influx type (required)">
            <NativeSelect value={rec.basis?.influxType}
                          onChange={(v) => setBasis({ influxType: v })}
                          options={INFLUX_TYPES.map((m) => ({ value: m, label: m }))}
                          testId="ks-basis-influxType" disabled={locked} />
          </Field>
          <Field label="Well control standard (optional)" helper="Reference training / regulatory standard, if any.">
            <NativeSelect value={rec.basis?.wellControlStandard ?? ""}
                          onChange={(v) => setBasis({ wellControlStandard: v })}
                          options={WELL_CONTROL_STANDARDS.map((s) => ({
                            value: s, label: s || "— not specified —",
                          }))}
                          testId="ks-basis-wellControlStandard" disabled={locked} />
          </Field>
        </div>
      </section>

      {/* 3. Source Snapshot ---------------------------------------------- */}
      <section className="card-rs p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h2 className="text-[13px] font-semibold">Source Snapshot</h2>
          {isNew && (
            <button type="button"
                    onClick={handleSynchronize}
                    disabled={locked}
                    data-testid="ks-synchronize-draft"
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed">
              <RefreshCcw className="w-3 h-3" /> Synchronize from session
            </button>
          )}
        </div>
        <p className="text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] leading-relaxed -mt-1 mb-3">
          Read-only mirror of the inherited values frozen on the kill sheet.
          Use <strong>Synchronize Engineering Data</strong> above to refresh
          every inheritable field from the active Engineering Session.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <SnapshotCell label="Current MD" value={rec.inputs?.currentMdFt} unit="ft" testId="ks-snap-md" />
          <SnapshotCell label="Current TVD" value={rec.inputs?.currentTvdFt} unit="ft" testId="ks-snap-tvd" />
          <SnapshotCell label="Hole size" value={rec.inputs?.holeSize} unit="in" testId="ks-snap-hole" />
          <SnapshotCell label="Casing shoe TVD" value={rec.inputs?.casingShoeTvdFt} unit="ft" testId="ks-snap-shoe" />
        </div>
        <div className="mt-2 text-[10px] text-[var(--rs-text-3,#6b7785)] uppercase tracking-[0.08em]">
          {rec.pullSnapshot?.pulledAt
            ? <>Last pulled {new Date(rec.pullSnapshot.pulledAt).toLocaleString()}
                {rec.pullSnapshot.pulledFrom?.bhaId ? <> · BHA {rec.pullSnapshot.pulledFrom.bhaId}</> : null}</>
            : <>Not yet pulled — values above are blank or manual.</>}
        </div>
      </section>

      {/* 4. Frozen Inputs ------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Frozen Inputs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Mud weight in (ppg)">
            <NumIn value={rec.inputs?.mwInPpg} onChange={(v) => setInputs({ mwInPpg: v })}
                   testId="ks-in-mwIn" disabled={locked} />
          </Field>
          <Field label="SIDPP (psi)">
            <NumIn value={rec.inputs?.sidppPsi} onChange={(v) => setInputs({ sidppPsi: v })}
                   testId="ks-in-sidpp" disabled={locked} />
          </Field>
          <Field label="SICP (psi)">
            <NumIn value={rec.inputs?.sicpPsi} onChange={(v) => setInputs({ sicpPsi: v })}
                   testId="ks-in-sicp" disabled={locked} />
          </Field>
          <Field label="Slow-pump pressure (psi)">
            <NumIn value={rec.inputs?.slowPumpPressurePsi} onChange={(v) => setInputs({ slowPumpPressurePsi: v })}
                   testId="ks-in-scr" disabled={locked} />
          </Field>
          <Field label="Pump rate (SPM)">
            <NumIn value={rec.inputs?.pumpRateSpm} onChange={(v) => setInputs({ pumpRateSpm: v })}
                   testId="ks-in-spm" disabled={locked} />
          </Field>
          <Field label="Size of influx (bbl)"
                 helper="Operator reading at the time of the kick. Stored for the next-release influx work; no calculator consumes it yet.">
            <NumIn value={rec.inputs?.influxSizeBbl} onChange={(v) => setInputs({ influxSizeBbl: v })}
                   testId="ks-in-influxSize" disabled={locked} />
          </Field>
          <Field label="Pump output (bbl/stk)">
            <NumIn value={rec.inputs?.pumpOutputBblStk} onChange={(v) => setInputs({ pumpOutputBblStk: v })}
                   testId="ks-in-pumpOut" disabled={locked} />
          </Field>
          <Field label="String internal volume (bbl)" helper="Surface→bit volume.">
            <NumIn value={rec.inputs?.stringInternalVolBbl} onChange={(v) => setInputs({ stringInternalVolBbl: v })}
                   testId="ks-in-stringVol" disabled={locked} />
          </Field>
          <Field label="Bit→shoe volume (bbl)">
            <NumIn value={rec.inputs?.bitToShoeVolBbl} onChange={(v) => setInputs({ bitToShoeVolBbl: v })}
                   testId="ks-in-b2sVol" disabled={locked} />
          </Field>
          <Field label="Shoe→surface volume (bbl)">
            <NumIn value={rec.inputs?.shoeToSurfaceVolBbl} onChange={(v) => setInputs({ shoeToSurfaceVolBbl: v })}
                   testId="ks-in-s2sVol" disabled={locked} />
          </Field>
          <Field label="Safety margin (ppg)">
            <NumIn value={rec.inputs?.safetyMarginPpg} onChange={(v) => setInputs({ safetyMarginPpg: v })}
                   testId="ks-in-safety" disabled={locked} />
          </Field>
          <Field label="Schedule increment (strokes)"
                 helper="Driller's Schedule row spacing. Default 100. Lower = finer step-down.">
            <NumIn value={rec.inputs?.scheduleIntervalStrokes}
                   onChange={(v) => setInputs({ scheduleIntervalStrokes: v })}
                   testId="ks-in-scheduleInterval" disabled={locked} />
          </Field>
          <Field label="LOT surface pressure (psi)"
                 helper="Optional. Recorded at leak-off test. Enables MAASP calculation.">
            <NumIn value={rec.inputs?.lotPressurePsi}
                   onChange={(v) => setInputs({ lotPressurePsi: v })}
                   testId="ks-in-lotPressure" disabled={locked} />
          </Field>
          <Field label="Mud weight at LOT (ppg)"
                 helper="Optional. Mud density in the hole during the leak-off test.">
            <NumIn value={rec.inputs?.mwAtLotPpg}
                   onChange={(v) => setInputs({ mwAtLotPpg: v })}
                   testId="ks-in-mwAtLot" disabled={locked} />
          </Field>
        </div>
      </section>

      {/* 5. Outputs — Pro-only computed output. Every input section above
          (Identity, Engineering Basis, Source Snapshot, Frozen Inputs) stays
          fully free/editable; only the derived live outputs, MAASP panel,
          Driller's Schedule and Calculation Trace are withheld here. */}
      {!pro && (
        <section className="card-rs p-4">
          <h2 className="text-[13px] font-semibold mb-3">Live Outputs</h2>
          <PremiumResultsLock
            label="live kill sheet outputs (KMW, ICP, FCP, strokes/times, MAASP, driller's schedule, calculation trace)"
            onUnlock={() => setUpgradeOpen(true)}
          />
        </section>
      )}
      {pro && (
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Live Outputs</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <OutCell label="KMW" value={_fmt(liveOutputs.kmwPpg, 2)} unit="ppg" testId="ks-out-kmw" />
          <OutCell label="Initial Circulating Pressure (ICP)" value={_fmtInt(liveOutputs.icpPsi)} unit="psi" testId="ks-out-icp" />
          <OutCell label="Final Circulating Pressure (FCP)" value={_fmtInt(liveOutputs.fcpPsi)} unit="psi" testId="ks-out-fcp" />
          <OutCell label="Volume · drill string internal" value={_fmt(liveOutputs.volStringInternalBbl, 2)} unit="bbl" testId="ks-out-volSB" />
          <OutCell label="Strokes surface→bit" value={_fmtInt(liveOutputs.strokesSurfaceToBit)} unit="stk" testId="ks-out-stkSB" />
          <OutCell label="Time surface→bit"    value={_fmt(liveOutputs.timeSurfaceToBitMin, 1)} unit="min" testId="ks-out-tSB" />
          <OutCell label="Volume · annulus bit→shoe" value={_fmt(liveOutputs.volBitToShoeBbl, 2)} unit="bbl" testId="ks-out-volBS" />
          <OutCell label="Strokes bit→shoe"    value={_fmtInt(liveOutputs.strokesBitToShoe)}    unit="stk" testId="ks-out-stkBS" />
          <OutCell label="Time bit→shoe"        value={_fmt(liveOutputs.timeBitToShoeMin, 1)}    unit="min" testId="ks-out-tBS" />
          <OutCell label="Volume · annulus shoe→rig floor" value={_fmt(liveOutputs.volShoeToSurfaceBbl, 2)} unit="bbl" testId="ks-out-volSS" />
          <OutCell label="Strokes shoe→surface" value={_fmtInt(liveOutputs.strokesShoeToSurface)} unit="stk" testId="ks-out-stkSS" />
          <OutCell label="Time shoe→surface"    value={_fmt(liveOutputs.timeShoeToSurfaceMin, 1)} unit="min" testId="ks-out-tSS" />
        </div>
        {liveOutputs.notes?.length > 0 && (
          <ul className="mt-3 text-[11px] text-amber-300 list-disc list-inside space-y-0.5"
              data-testid="ks-out-notes">
            {liveOutputs.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
      </section>
      )}

      {/* 5.5 MAASP / Pressure Limits ------------------------------------ */}
      {pro && <MaaspPanel outputs={liveOutputs} />}

      {/* 6. Driller's Schedule -------------------------------------------- */}
      {pro && (
      <section className="card-rs">
        <button type="button"
                onClick={() => setScheduleOpen((o) => !o)}
                data-testid="ks-schedule-toggle"
                aria-expanded={scheduleOpen}
                className="w-full flex items-center justify-between p-4 text-left">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
              Step-down · {(liveOutputs.schedule?.method || rec.basis?.killMethod || "wait-and-weight").replace(/-/g, " ")}
            </div>
            <div className="text-[13px] font-semibold mt-0.5">
              Driller&apos;s Schedule
            </div>
          </div>
          {scheduleOpen
            ? <ChevronDown className="w-4 h-4 text-amber-400" />
            : <ChevronRight className="w-4 h-4 text-amber-400" />}
        </button>
        {scheduleOpen && (
          <div data-testid="ks-schedule-panel" className="px-4 pb-4 -mt-1">
            {liveOutputs.schedule?.ok ? (
              <>
                <ScheduleTable schedule={liveOutputs.schedule}
                               surfaceLineRow={surfaceLineRow} />
                <div className="mt-3 rounded-md border border-[var(--rs-border,#1f2630)] p-3 text-[var(--rs-text,#e8edf2)]">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rs-text-3,#6b7785)] mb-2">
                    Step-down chart
                  </div>
                  <ScheduleChart schedule={liveOutputs.schedule}
                                 maaspInitialPsi={liveOutputs.maaspInitialPsi} />
                </div>
              </>
            ) : (
              <div data-testid="ks-schedule-empty"
                   className="rounded-md border border-amber-500/30 p-3 text-[11.5px] text-amber-200/90 leading-relaxed">
                <div className="font-semibold mb-1">Schedule unavailable — fill in:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {(liveOutputs.schedule?.notes || []).map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>
      )}

      {/* 7. Engineering Details / Calculation Trace --------------------- */}
      {pro && (
      <section className="card-rs">
        <button type="button"
                onClick={() => setTraceOpen((o) => !o)}
                data-testid="ks-trace-toggle"
                aria-expanded={traceOpen}
                className="w-full flex items-center justify-between p-4 text-left">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
              Calculation provenance
            </div>
            <div className="text-[13px] font-semibold mt-0.5">
              Engineering Details · Calculation Trace
            </div>
          </div>
          {traceOpen
            ? <ChevronDown className="w-4 h-4 text-amber-400" />
            : <ChevronRight className="w-4 h-4 text-amber-400" />}
        </button>
        {traceOpen && (
          <div data-testid="ks-trace-panel" className="px-4 pb-4 -mt-1">
            <div className="text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] mb-3 leading-relaxed">
              Every output below carries the exact formula and frozen inputs
              that produced it. Versions identify the deterministic engine
              that ran the math — they change only when a calculation method
              is updated.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <Provenance label="Formulas version" value={liveOutputs.provenance?.formulasVersion} testId="ks-trace-formulasVersion" />
              <Provenance label="Units version"    value={liveOutputs.provenance?.unitsVersion}    testId="ks-trace-unitsVersion" />
              <Provenance label="Computed at"
                          value={liveOutputs.provenance?.computedAt
                            ? new Date(liveOutputs.provenance.computedAt).toLocaleString()
                            : "—"}
                          testId="ks-trace-computedAt" />
            </div>
            <div className="flex flex-col gap-2" data-testid="ks-trace-list">
              {Object.entries(liveOutputs.trace || {}).map(([key, { formula, inputs }]) => (
                <TraceRow key={key} outputKey={key} formula={formula} inputs={inputs} />
              ))}
            </div>
          </div>
        )}
      </section>
      )}

      {/* 7. Notes ---------------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Notes</h2>
        <Field label="Free-text notes">
          <TextArea value={rec.notes} onChange={(v) => set({ notes: v })}
                    testId="ks-notes" rows={3} />
        </Field>
      </section>

      {/* RC-1 Editor Save Consistency — primary Save anchored at the bottom.
          Approve Sheet and Lock Sheet were intentionally removed from the
          operator UI (Legacy `approveKillSheet` still exists in
          `killsheet/storage.js` for back-compat + tests). */}
      <div data-testid="ks-editor-bottom-bar"
           className="card-rs p-4 flex flex-wrap items-center justify-end gap-2">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/kill-sheets`)}
                data-testid="ks-editor-back-bottom"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[var(--rs-text-2)] border border-[var(--rs-border,#1f2630)] hover:bg-[var(--rs-surface-2,#12161d)] order-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        {!isNew && (rec.status === "approved" || rec.status === "active" || rec.status === "locked") && (
          <button type="button"
                  onClick={handleCreateRevision}
                  data-testid="ks-create-revision"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 order-3">
            <GitBranch className="w-3.5 h-3.5" /> Create Revision
          </button>
        )}
        {!isNew && (
          <Link to={`/wells/${wellId}/kill-sheets/${ksId}/print`}
                data-testid="ks-editor-print"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-amber-300 border border-amber-500/30 hover:bg-amber-500/10 order-2">
            <Printer className="w-3.5 h-3.5" /> Print
          </Link>
        )}
        <button type="button"
                onClick={handleSave}
                data-testid="ks-editor-save"
                disabled={locked}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed order-1"
                style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E" }}>
          <Save className="w-4 h-4" /> Save Kill Sheet
        </button>
      </div>

      {/* RC-1 — Floating Engineering Toolbar (Save · Synchronize · Print). */}
      <FloatingActionToolbar
        testId="ks-fab"
        actions={[
          { id: "save", icon: Save, label: "Save", onClick: handleSave,
            testId: "fab-ks-save", primary: true, disabled: locked },
          { id: "sync", icon: RefreshCcw, label: "Synchronize",
            onClick: !isNew && !locked ? handleSynchronize : null, testId: "fab-ks-sync" },
          { id: "print", icon: Printer, label: "Print",
            href: !isNew ? `/wells/${wellId}/kill-sheets/${ksId}/print` : null,
            testId: "fab-ks-print" },
        ]}
      />

      <UnsavedChangesDialog
        open={unsaved.dialogOpen}
        onSaveAndLeave={unsaved.handleSaveAndLeave}
        onDiscard={unsaved.handleDiscard}
        onCancel={unsaved.handleCancel}
      />

      <SubscribeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}

// =============================================================================
// Small cells
// =============================================================================
function SnapshotCell({ label, value, unit, testId }) {
  const has = value !== "" && value != null && Number.isFinite(Number(value));
  return (
    <div data-testid={testId}
         className="rounded-md border border-[var(--rs-border,#1f2630)] bg-[rgba(255,255,255,0.015)] p-2.5">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--rs-text-3,#6b7785)]">{label}</div>
      <div className="mt-1 text-[12px] font-medium text-[var(--rs-text,#e8edf2)] font-mono">
        {has ? `${Number(value).toLocaleString()} ${unit}` : "—"}
      </div>
    </div>
  );
}

function OutCell({ label, value, unit, testId }) {
  return (
    <div data-testid={testId}
         className="rounded-md border border-amber-500/20 bg-[rgba(245,180,0,0.04)] p-3">
      <div className="text-[9px] uppercase tracking-[0.16em] text-amber-400/80">{label}</div>
      <div className="mt-1 text-[16px] font-semibold text-amber-100 font-mono">
        {value} <span className="text-[10px] text-amber-300/70 font-normal">{unit}</span>
      </div>
    </div>
  );
}

function Provenance({ label, value, testId }) {
  return (
    <div data-testid={testId}
         className="rounded-md border border-[var(--rs-border,#1f2630)] p-2">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--rs-text-3,#6b7785)]">{label}</div>
      <div className="mt-0.5 text-[10.5px] font-mono text-[var(--rs-text-2,#c2cad4)] break-all">
        {value || "—"}
      </div>
    </div>
  );
}

// "What Changed" diff strip — Pass E.
// Renders a concise comparison vs the parent revision. Always visible
// on the editor when a parent exists; print page renders an equivalent block.
function WhatChangedStrip({ diff, rec }) {
  const fmt = (v) => {
    if (v === "" || v == null) return "—";
    const n = Number(v);
    if (Number.isFinite(n)) {
      return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(v);
  };
  return (
    <section data-testid="ks-whatchanged"
             className="card-rs p-3 sm:p-4 border-sky-500/30">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="inline-flex items-center gap-2">
          <GitBranch className="w-3.5 h-3.5 text-sky-400" />
          <h2 className="text-[12px] font-semibold tracking-wide uppercase text-sky-300">
            What Changed vs Parent Revision
          </h2>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rs-text-dim,#9aa6b2)]">
          Rev {rec.revision || "?"} from parent {diff.parentRecord?.revision || "(unlabelled)"}
          {rec.revisionReason && (
            <> · Reason: <span className="text-sky-300">{REVISION_REASON_LABELS[rec.revisionReason] || rec.revisionReason}</span></>
          )}
        </div>
      </div>
      {diff.changes.length === 0 ? (
        <div data-testid="ks-whatchanged-empty"
             className="text-[11.5px] text-[var(--rs-text-dim,#9aa6b2)] italic">
          No engineering values differ from the parent. Edit a value or add a reason to populate the diff.
        </div>
      ) : (
        <div data-testid="ks-whatchanged-table" className="overflow-x-auto">
          <table className="w-full text-left text-[11.5px]">
            <thead>
              <tr className="text-[9.5px] uppercase tracking-[0.16em] text-[var(--rs-text-3,#6b7785)]">
                <th className="py-1.5 pr-3">Field</th>
                <th className="py-1.5 pr-3">Parent</th>
                <th className="py-1.5">Current</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {diff.changes.map((c) => (
                <tr key={c.field}
                    data-testid={`ks-whatchanged-row-${c.field}`}
                    className="border-t border-[var(--rs-border,#1f2630)]">
                  <td className="py-1.5 pr-3 font-sans text-[var(--rs-text,#e8edf2)]">{c.label}</td>
                  <td className="py-1.5 pr-3 text-[var(--rs-text-dim,#9aa6b2)] line-through">{fmt(c.parent)}</td>
                  <td className="py-1.5 text-sky-200 font-semibold">{fmt(c.child)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rec.revisionNotes && (
        <div className="mt-2 text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] italic">
          Revision notes: {rec.revisionNotes}
        </div>
      )}
    </section>
  );
}

// MAASP / Pressure Limits panel — high-visibility on-screen guardrail.
// When any guardrail flag is set we wrap the panel in a thick red border +
// bold "MAASP GUARDRAIL BREACH" banner so it's impossible to miss.
function MaaspPanel({ outputs }) {
  const g = outputs?.guardrails || {};
  const breach = g.kmwExceedsMamw || g.mwInExceedsMamw || g.maaspAtKmwNegative || g.sicpExceedsMaasp;
  const available = g.available;

  return (
    <section
      data-testid="ks-maasp-panel"
      data-breach={breach ? "true" : "false"}
      className={`card-rs p-4 ${breach ? "border-red-500/70 bg-red-500/5" : ""}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="text-[13px] font-semibold">MAASP · Pressure Limits</h2>
        {!available && (
          <span data-testid="ks-maasp-disabled"
                className="text-[9.5px] uppercase tracking-[0.18em] text-[var(--rs-text-3,#6b7785)]">
            Enter LOT pressure + MW@LOT to enable
          </span>
        )}
        {breach && (
          <span data-testid="ks-maasp-breach-flag"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-[0.18em] border-2 border-red-500 text-red-200 bg-red-500/20">
            <AlertTriangle className="w-3 h-3" /> MAASP GUARDRAIL BREACH
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <OutCell label="Max Allowable MW" value={_fmt(outputs?.maxAllowableMwPpg, 2)} unit="ppg" testId="ks-out-mamw" />
        <OutCell label="MAASP (initial)"   value={_fmtInt(outputs?.maaspInitialPsi)}    unit="psi" testId="ks-out-maaspInit" />
        <OutCell label="MAASP at KMW"      value={_fmtInt(outputs?.maaspAtKmwPsi)}      unit="psi" testId="ks-out-maaspKmw" />
      </div>

      {breach && (
        <ul data-testid="ks-maasp-messages"
            className="mt-3 text-[12px] text-red-200 font-medium list-disc list-inside space-y-1">
          {g.criticalMessages.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
    </section>
  );
}

function TraceRow({ outputKey, formula, inputs }) {
  return (
    <div data-testid={`ks-trace-row-${outputKey}`}
         className="rounded-md border border-[var(--rs-border,#1f2630)] p-2.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11.5px] font-semibold text-[var(--rs-text,#e8edf2)]">{outputKey}</span>
        <code className="text-[10.5px] font-mono text-amber-300 text-right">{formula}</code>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
        {Object.entries(inputs || {}).map(([k, v]) => (
          <span key={k} className="text-[10.5px] font-mono text-[var(--rs-text-dim,#9aa6b2)]">
            <span className="text-[var(--rs-text-3,#6b7785)]">{k}=</span>
            <span className="text-[var(--rs-text-2,#c2cad4)]">
              {v === "" || v == null ? "—" : String(v)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Driller's Schedule table — readable under field conditions.
// Large, high-contrast numbers + phase color coding. The print layout
// (Pass C) will share this component via a `denseForPrint` prop.
function ScheduleTable({ schedule, surfaceLineRow }) {
  const rows = schedule.rows || [];
  const firstKillIdx = firstKillRowIndex(rows);
  // Surface → Bit → Shoe → Surface milestones (display only — indices into
  // the SAME rows array the engine returned; no row/value invented here).
  // "Last row of a given phase" is the milestone anchor for that phase —
  // robust to rounding, and correct whether or not the shoe/surface
  // extension exists for this sheet (bitToShoeIdx/shoeToSurfaceIdx stay -1
  // when those segments weren't computed upstream).
  const lastKillIdx          = rows.reduce((acc, r, idx) => (r.phase === "kill" ? idx : acc), -1);
  const lastBitToShoeIdx     = rows.reduce((acc, r, idx) => (r.phase === "bit-to-shoe" ? idx : acc), -1);
  const lastShoeToSurfaceIdx = rows.reduce((acc, r, idx) => (r.phase === "shoe-to-surface" ? idx : acc), -1);
  return (
    <div data-testid="ks-schedule-table">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2 text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)]">
        <span>
          {rows.length} step{rows.length === 1 ? "" : "s"} · every {schedule.intervalStrokes} strokes
        </span>
        <span data-testid="ks-schedule-method">
          Method: <span className="text-amber-300">{schedule.method}</span>
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-[var(--rs-border,#1f2630)]">
        <table className="w-full min-w-[600px] text-left">
          <thead className="bg-[rgba(245,180,0,0.06)]">
            <tr className="text-[9.5px] uppercase tracking-[0.18em] text-amber-400/90">
              <th className="px-3 py-2">Stroke</th>
              <th className="px-3 py-2 text-right">Standpipe Pressure (psi)</th>
              <th className="px-3 py-2 text-right">Time (min)</th>
              <th className="px-3 py-2">Phase · ICP / FCP / Milestone</th>
            </tr>
          </thead>
          <tbody className="font-mono text-[var(--rs-text,#e8edf2)]">
            {rows.map((r, i) => {
              // Presentation-only ICP/FCP + Surface→Bit→Shoe→Surface
              // milestone identification (P0 display correction) — the
              // first row IS the ICP, the last "kill"-phase row IS the FCP
              // (kill mud reaching the bit), the last "bit-to-shoe" row IS
              // kill mud reaching the shoe, and the last "shoe-to-surface"
              // row IS kill mud reaching surface. No calculation or
              // schedule math touched — purely which badge renders where.
              const isIcpRow     = i === 0;
              const isFcpRow     = i === lastKillIdx && lastKillIdx > 0;
              const isShoeRow    = lastBitToShoeIdx !== -1 && i === lastBitToShoeIdx;
              const isSurfaceRow = lastShoeToSurfaceIdx !== -1 && i === lastShoeToSurfaceIdx;
              const isMilestone  = isIcpRow || isFcpRow || isShoeRow || isSurfaceRow;
              // The surface-line row is inserted immediately BEFORE the
              // method's first kill-mud row (stroke zero), after any
              // preceding circulation rows.
              const isFirstKillRow = i === firstKillIdx;
              return (
                <React.Fragment key={i}>
                {isFirstKillRow && surfaceLineRow && (
                  <tr data-testid="ks-schedule-surface-line-row"
                      className="border-t border-[var(--rs-border,#1f2630)] bg-[rgba(245,180,0,0.05)]">
                    <td className="px-3 py-2 text-[14px] font-semibold align-top">
                      <span data-testid="ks-schedule-surface-strokes">
                        {surfaceLineRow.strokes == null ? "—" : surfaceLineRow.strokes.toFixed(2)}
                      </span>
                      <div className="text-[9.5px] font-normal text-[var(--rs-text-dim,#9aa6b2)] mt-0.5 leading-snug">
                        strokes required
                        {surfaceLineRow.wholeStrokes != null && (
                          <> · whole stroke {surfaceLineRow.wholeStrokes.toLocaleString()}</>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-[15px] font-semibold text-amber-100 align-top">
                      —
                      <div className="text-[9px] font-normal normal-case text-[var(--rs-text-dim,#9aa6b2)] mt-0.5 leading-snug">
                        no scheduled pressure target
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-[13px] align-top">
                      {surfaceLineRow.timeMin == null ? "—" : surfaceLineRow.timeMin.toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-[11px] align-top">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.14em] border border-amber-500/40 text-amber-300">
                        surface line
                      </span>
                      <div data-testid="ks-schedule-surface-label"
                           className="mt-1 text-[10.5px] font-semibold text-[var(--rs-text,#e8edf2)] not-italic whitespace-normal">
                        {SURFACE_LINE_ROW_LABEL}
                      </div>
                      <div data-testid="ks-schedule-surface-volume"
                           className="mt-0.5 text-[10px] text-[var(--rs-text-2,#c2cad4)] not-italic whitespace-normal">
                        Surface-line volume:{" "}
                        <span className="font-semibold">
                          {surfaceLineRow.volumeValue == null
                            ? "—"
                            : `${Number(surfaceLineRow.volumeValue).toFixed(2)} ${surfaceLineRow.volumeUnit}`}
                        </span>{" "}
                        ({surfaceLineRow.sourceNote})
                      </div>
                      {!surfaceLineRow.ok && (
                        <div data-testid="ks-schedule-surface-message"
                             className="mt-0.5 text-[10px] text-amber-300 not-italic whitespace-normal">
                          {surfaceLineRow.message}
                        </div>
                      )}
                      <div data-testid="ks-schedule-surface-reset-reminder"
                           className="mt-1.5 text-[10px] font-bold uppercase not-italic whitespace-normal leading-snug"
                           style={{ color: "#e02020" }}>
                        {SURFACE_LINE_RESET_REMINDER}
                      </div>
                    </td>
                  </tr>
                )}
                <tr
                    data-testid={`ks-schedule-row-${i}`}
                    className={`border-t border-[var(--rs-border,#1f2630)] ${
                      r.phase === "circulate" ? "bg-[rgba(56,189,248,0.04)]" : ""
                    }`}>
                  <td className="px-3 py-2 text-[14px] font-semibold">
                    {r.stroke?.toLocaleString() ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-[15px] font-semibold text-amber-100">
                    {r.pressurePsi?.toLocaleString() ?? "—"}
                    {isIcpRow && (
                      <div data-testid="ks-schedule-icp-badge"
                           className="mt-1 inline-block whitespace-normal max-w-[130px] px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.10em] leading-snug border border-emerald-500/50 text-emerald-300">
                        Initial Circulating Pressure (ICP) · {r.pressurePsi?.toLocaleString() ?? "—"} psi
                      </div>
                    )}
                    {isFcpRow && (
                      <div data-testid="ks-schedule-fcp-badge"
                           className="mt-1 inline-block whitespace-normal max-w-[130px] px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.10em] leading-snug border border-rose-500/50 text-rose-300">
                        Final Circulating Pressure (FCP) · {r.pressurePsi?.toLocaleString() ?? "—"} psi
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-[13px]">
                    {r.timeMin == null ? "—" : r.timeMin.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.14em] border ${
                      r.phase === "circulate"
                        ? "border-sky-500/40 text-sky-300"
                        : "border-amber-500/40 text-amber-300"
                    }`}>
                      {r.phase}
                    </span>
                    {isFcpRow && (
                      <div data-testid="ks-schedule-milestone-bit"
                           className="mt-1 text-[10px] text-rose-300 font-semibold not-italic">
                        Kill mud reaches bit
                      </div>
                    )}
                    {isShoeRow && (
                      <div data-testid="ks-schedule-milestone-shoe"
                           className="mt-1 text-[10px] text-amber-300 font-semibold not-italic">
                        Kill mud reaches shoe
                      </div>
                    )}
                    {isSurfaceRow && (
                      <div data-testid="ks-schedule-milestone-surface"
                           className="mt-1 text-[10px] text-amber-300 font-semibold not-italic">
                        Kill mud reaches surface
                      </div>
                    )}
                    {!isMilestone && (
                      <div className="mt-1 text-[10px] text-[var(--rs-text-dim,#9aa6b2)] not-italic">
                        Scheduled target
                      </div>
                    )}
                    {isFirstKillRow && (
                      <div data-testid="ks-schedule-drillstring-zero"
                           className="mt-1 text-[10px] font-semibold text-amber-200 not-italic whitespace-normal">
                        {DRILL_STRING_ZERO_LABEL}
                      </div>
                    )}
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
